<template>
  <div :class="rootClasses">
    <div class="war-room-achievement__border-overlay" />
    <div class="war-room-achievement__container">
      <div class="war-room-achievement__text">
        {{ title }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { achievementLevels, achievementSizes } from '~/components/partials/war-room/constants';

export default Vue.extend({
  name: 'WarRoomAchievement',
  props: {
    title: {
      type: String,
      default: '',
      required: true,
    },
    level: {
      type: String,
      default: achievementLevels.GOLD,
      required: true,
      validator: (level: string) => Object.values(achievementLevels).includes(level),
    },
    size: {
      type: String,
      default: achievementSizes.SMALL,
      validator: (size: string) => Object.values(achievementSizes).includes(size),
    },
  },
  computed: {
    rootClasses(): string[] {
      return ['war-room-achievement', `war-room-achievement--${this.level}`, `war-room-achievement--${this.size}`];
    },
  },
});
</script>

<style scoped lang="scss">
@mixin font-color-gradient($color1, $color2) {
  // fallback color;

  color: var(--black-a70);
  background: linear-gradient(180deg, rgba($color1, 0.9) 0%, rgba($color2, 0.9) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.war-room-achievement {
  $this: &;

  height: max-content;
  position: relative;
  padding: 1px;
  border-radius: 8px;
  overflow: hidden;

  &__border-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
  }

  &__container {
    position: relative;
    padding: 10px;
    border-radius: inherit;
    background: inherit;
    z-index: 2;
  }

  &__text {
    @include inherit-font;

    white-space: nowrap;
  }

  &--small {
    @include caption3-semibold;

    border-radius: 4px;

    #{$this}__container {
      padding: 1px 5px;
    }
  }

  &--medium {
    @include subhead-semibold;

    box-shadow: var(--theme-elevation-01);
    border-radius: 8px;

    #{$this}__container {
      padding: 5px 10px;
    }
  }

  &--large {
    @include subhead-semibold;

    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.07), 0 1px 2px rgba(0, 0, 0, 0.1);
    border-radius: 8px;

    @include mq('sm') {
      @include body-semibold;
    }

    #{$this}__container {
      padding: 7px 10px;
    }
  }

  &--gold {
    background: linear-gradient(111.96deg, #e3bc5a 0%, #e9d8ab 25%, #f4e9c4 50%, #e9d8ab 75%, #e3bc5a 100%);

    #{$this}__text {
      @include font-color-gradient(#2e2406, #9e640d);
    }

    #{$this}__border-overlay {
      background: linear-gradient(180deg, rgba(114, 85, 43, 0.15) 0%, rgba(40, 22, 5, 0.15) 100%);
    }
  }

  &--silver {
    background: linear-gradient(103.18deg, #bfc1c4 0%, #dadcde 34.11%, #e7e9ec 65.07%, #bcbebf 100%);

    #{$this}__text {
      @include font-color-gradient(#282b38, #3d4772);
    }

    #{$this}__border-overlay {
      background: linear-gradient(180deg, rgba(74, 91, 154, 0.15) 0%, rgba(39, 47, 86, 0.15) 100%);
    }
  }

  &--bronze {
    background: linear-gradient(111.96deg, #cf9a8c 0%, #eabcb1 24.38%, #f5c9c0 50%, #eabcb1 77.15%, #cf9a8c 100%);

    #{$this}__text {
      @include font-color-gradient(#370b08, #845550);
    }

    #{$this}__border-overlay {
      background: linear-gradient(180deg, rgba(162, 89, 94, 0.15) 0%, rgba(43, 7, 52, 0.15) 100%);
    }
  }

  &--platinum {
    background: conic-gradient(
      from 171.52deg at 50% 50%,
      #f0f1f6 0deg,
      #a0b9cc 90deg,
      #dde5f0 180deg,
      #c5e6f9 234.26deg,
      #a7b5bb 270deg,
      #ecf1f4 360deg
    );

    #{$this}__text {
      @include font-color-gradient(#070f14, #5c656a);
    }

    #{$this}__border-overlay {
      background: linear-gradient(180deg, rgba(42, 17, 78, 0.15) 0%, rgba(25, 10, 48, 0.15) 100%);
    }
  }

  &--no_level {
    color: var(--gray-50);
    border: 1px solid var(--theme-color-border);
  }
}
</style>
